import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Glasses, ShieldCheck, Phone, Mail, Calendar, Plus, History, FolderOpen, Download, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import ClienteForm from "../components/ClienteForm";
import PrescrizioneForm from "../components/PrescrizioneForm";
import DocumentiCliente from "../components/DocumentiCliente";

const Row = ({ label, value }) => (
  <div className="flex justify-between py-2.5 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-900">{value || "—"}</span>
  </div>
);

const Consenso = ({ label, ok }) => (
  <div className={`flex items-center justify-between px-4 py-3 rounded-lg border ${ok ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
    <span className="text-sm font-medium text-slate-700">{label}</span>
    <Badge className={ok ? "bg-emerald-600 hover:bg-emerald-600" : "bg-slate-300 hover:bg-slate-300"}>{ok ? "Attivo" : "Non attivo"}</Badge>
  </div>
);

export default function ClienteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [presc, setPresc] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [openPresc, setOpenPresc] = useState(false);
  const [editPresc, setEditPresc] = useState(null);
  const [delPresc, setDelPresc] = useState(null);
  const { isAdmin } = useAuth();

  const exportGdpr = async () => {
    try {
      const res = await api.get(`/export/cliente/${id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gdpr_cliente_${cliente?.cognome || ""}_${id}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Esportazione GDPR completata");
    } catch (e) {
      toast.error("Esportazione non consentita");
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clienti/${id}`);
      setCliente(data);
      const p = await api.get(`/clienti/${id}/prescrizioni`);
      setPresc(p.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const doDeletePresc = async () => {
    try {
      await api.delete(`/prescrizioni/${delPresc.id}`);
      toast.success("Prescrizione eliminata");
      setDelPresc(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!cliente) return <div className="text-slate-500">Caricamento…</div>;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/clienti")} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-clienti">
        <ArrowLeft className="h-4 w-4" /> Torna ai clienti
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-[#0F172A] text-white flex items-center justify-center font-head text-xl font-bold">
            {cliente.nome?.[0]}{cliente.cognome?.[0]}
          </div>
          <div>
            <h1 className="font-head text-3xl font-bold tracking-tight text-slate-900">{cliente.cognome} {cliente.nome}</h1>
            <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
              <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {cliente.telefono || "—"}</span>
              <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {cliente.email || "—"}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button data-testid="export-cliente-gdpr-btn" onClick={exportGdpr} variant="outline" className="h-11">
              <Download className="h-4 w-4 mr-2" /> Esporta GDPR
            </Button>
          )}
          <Button data-testid="edit-cliente-btn" onClick={() => setOpenEdit(true)} variant="outline" className="h-11">
            <Pencil className="h-4 w-4 mr-2" /> Modifica
          </Button>
        </div>
      </div>

      <Tabs defaultValue="anagrafica">
        <TabsList data-testid="cliente-tabs">
          <TabsTrigger value="anagrafica" data-testid="tab-anagrafica">Anagrafica & Consensi</TabsTrigger>
          <TabsTrigger value="ottica" data-testid="tab-ottica"><Glasses className="h-4 w-4 mr-1.5" /> Dati Ottici</TabsTrigger>
          <TabsTrigger value="documenti" data-testid="tab-documenti"><FolderOpen className="h-4 w-4 mr-1.5" /> Documenti</TabsTrigger>
        </TabsList>

        <TabsContent value="anagrafica" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6">
              <h3 className="font-head text-lg font-semibold text-slate-900 mb-3">Dati anagrafici</h3>
              <Row label="Nome" value={cliente.nome} />
              <Row label="Cognome" value={cliente.cognome} />
              <Row label="Data di nascita" value={cliente.data_nascita} />
              <Row label="Telefono" value={cliente.telefono} />
              <Row label="Email" value={cliente.email} />
              <Row label="Note" value={cliente.note} />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 space-y-3">
              <h3 className="font-head text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" /> Stato consensi</h3>
              <Consenso label="Consenso marketing" ok={cliente.consenso_marketing} />
              <Consenso label="Consenso WhatsApp" ok={cliente.consenso_whatsapp} />
              <Consenso label="Consenso email" ok={cliente.consenso_email} />
              <div className="pt-2 text-xs text-slate-500 space-y-1">
                <div>Data consenso: <span className="font-medium text-slate-700">{cliente.data_consenso || "—"}</span></div>
                <div>Modalità raccolta: <span className="font-medium text-slate-700">{cliente.modalita_raccolta || "—"}</span></div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 flex items-center gap-4">
              <div className="h-11 w-11 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><Calendar className="h-5 w-5" /></div>
              <div><div className="text-xs uppercase tracking-wider text-slate-500">Ultimo controllo</div><div className="font-head text-lg font-semibold">{cliente.data_ultimo_controllo || "—"}</div></div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-6 flex items-center gap-4">
              <div className="h-11 w-11 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center"><Calendar className="h-5 w-5" /></div>
              <div><div className="text-xs uppercase tracking-wider text-slate-500">Ultimo acquisto</div><div className="font-head text-lg font-semibold">{cliente.data_ultimo_acquisto || "—"}</div></div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ottica" className="space-y-6 mt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-slate-500"><ShieldCheck className="h-4 w-4 text-[#1E40AF]" /> Area dati ottici — archiviata separatamente, non usata dal marketing.</div>
            <Button data-testid="new-prescrizione-btn" onClick={() => setOpenPresc(true)} className="bg-[#1E40AF] hover:bg-[#1D4ED8]">
              <Plus className="h-4 w-4 mr-2" /> Nuova prescrizione
            </Button>
          </div>

          {presc.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">Nessuna prescrizione registrata.</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><History className="h-4 w-4" /> Storico prescrizioni ({presc.length})</div>
              {presc.map((p, i) => (
                <div key={p.id} data-testid={`prescrizione-${p.id}`} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-b border-slate-200">
                    <div className="text-sm font-medium text-slate-900">Controllo del {p.data_controllo}</div>
                    <div className="flex items-center gap-2">
                      {i === 0 && <Badge className="bg-[#1E40AF] hover:bg-[#1E40AF]">Attuale</Badge>}
                      {p.tipo_lente && <span className="text-xs text-slate-500">{p.tipo_lente}</span>}
                      <button data-testid={`edit-prescrizione-${p.id}`} onClick={() => setEditPresc(p)} className="p-1.5 rounded-lg text-slate-400 hover:text-[#1E40AF] hover:bg-white transition-colors" title="Modifica prescrizione">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button data-testid={`delete-prescrizione-${p.id}`} onClick={() => setDelPresc(p)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-white transition-colors" title="Elimina prescrizione">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="p-5 grid grid-cols-2 gap-4">
                    {[["OD (Destro)", p.od_sfera, p.od_cilindro, p.od_asse, p.od_add], ["OS (Sinistro)", p.os_sfera, p.os_cilindro, p.os_asse, p.os_add]].map(([eye, sf, cil, asse, add]) => (
                      <div key={eye} className="rounded-lg border border-slate-200 p-4">
                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{eye}</div>
                        <div className="grid grid-cols-4 gap-2 font-mono text-sm">
                          <div><span className="text-slate-400 text-xs block">SFERA</span>{sf || "—"}</div>
                          <div><span className="text-slate-400 text-xs block">CIL</span>{cil || "—"}</div>
                          <div><span className="text-slate-400 text-xs block">ASSE</span>{asse || "—"}</div>
                          <div><span className="text-slate-400 text-xs block">ADD</span>{add || "—"}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {p.note && <div className="px-5 pb-4 text-sm text-slate-600">Note: {p.note}</div>}
                  <div className="px-5 pb-3 text-xs text-slate-400">Registrato da {p.created_by}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documenti" className="mt-6">
          <DocumentiCliente clienteId={id} />
        </TabsContent>
      </Tabs>

      <ClienteForm open={openEdit} onOpenChange={setOpenEdit} cliente={cliente} onSaved={load} />
      <PrescrizioneForm open={openPresc} onOpenChange={setOpenPresc} clienteId={id} onSaved={load} />
      <PrescrizioneForm open={!!editPresc} onOpenChange={(v) => !v && setEditPresc(null)} clienteId={id} prescrizione={editPresc} onSaved={load} />

      <AlertDialog open={!!delPresc} onOpenChange={(v) => !v && setDelPresc(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la prescrizione?</AlertDialogTitle>
            <AlertDialogDescription>
              La prescrizione del controllo {delPresc?.data_controllo} verrà rimossa dallo storico. L'operazione è tracciata nel log attività ed è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-prescrizione" onClick={doDeletePresc} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
